import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck, ShieldX, Loader2 } from "lucide-react";
import axios from "axios";
import { fmtDate } from "@/utils/datetime";

const BASE = process.env.REACT_APP_BACKEND_URL;

/** Public certificate verification — NO auth. Renders only minimal, non-sensitive info. */
export default function Verify() {
    const { t, i18n } = useTranslation();
    const { certNumber } = useParams();
    const [state, setState] = useState<"loading" | "ok" | "notfound" | "error">("loading");
    const [data, setData] = useState<any>(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${BASE}/api/verify/${encodeURIComponent(certNumber || "")}`);
                setData(data); setState("ok");
            } catch (e: any) {
                if (e?.response?.status === 404) setState("notfound");
                else setState("error");
            }
        })();
    }, [certNumber]);

    return (
        <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{ background: "var(--page)", color: "var(--text)" }}>
            <div className="w-full max-w-md rounded-xl border p-6" style={{ background: "var(--card)", borderColor: "var(--border)" }} data-testid="verify-card">
                {state === "loading" && <div className="flex flex-col items-center gap-3 py-6"><Loader2 size={28} className="animate-spin" /><div className="text-sm" style={{ color: "var(--text-muted)" }}>{t("verify.loading")}</div></div>}
                {state === "notfound" && (
                    <div className="flex flex-col items-center gap-3 py-6" data-testid="verify-notfound">
                        <div className="rounded-full p-3" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}><ShieldX size={24} /></div>
                        <div className="text-lg font-semibold">{t("verify.notfound_title")}</div>
                        <div className="text-sm text-center" style={{ color: "var(--text-muted)" }}>{t("verify.notfound_body")}</div>
                    </div>
                )}
                {state === "error" && (
                    <div className="text-sm" style={{ color: "var(--error-fg)" }}>{t("verify.error")}</div>
                )}
                {state === "ok" && data && (
                    <div data-testid={data.status === "revoked" ? "verify-revoked" : "verify-valid"}>
                        <div className="flex items-center gap-2 mb-4">
                            {data.status === "issued" ? (
                                <div className="rounded-full p-2" style={{ background: "var(--success-bg)", color: "var(--success-fg)" }}><ShieldCheck size={20} /></div>
                            ) : (
                                <div className="rounded-full p-2" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}><ShieldX size={20} /></div>
                            )}
                            <div className="text-lg font-semibold">{data.status === "issued" ? t("verify.valid_title") : t("verify.revoked_title")}</div>
                        </div>
                        <dl className="text-sm space-y-2">
                            <div><dt className="text-xs" style={{ color: "var(--text-muted)" }}>{t("verify.cert_number")}</dt><dd className="font-mono">{data.cert_number}</dd></div>
                            <div><dt className="text-xs" style={{ color: "var(--text-muted)" }}>{t("verify.person")}</dt><dd>{data.person_name || "—"}</dd></div>
                            <div><dt className="text-xs" style={{ color: "var(--text-muted)" }}>{t("verify.program")}</dt><dd>{data.program_name || "—"}</dd></div>
                            <div><dt className="text-xs" style={{ color: "var(--text-muted)" }}>{t("verify.issued_at")}</dt><dd>{fmtDate(data.issued_at)}</dd></div>
                            <div><dt className="text-xs" style={{ color: "var(--text-muted)" }}>{t("common.status")}</dt><dd data-testid="verify-status">{t(`verify.status.${data.status}`)}</dd></div>
                        </dl>
                    </div>
                )}
            </div>
        </div>
    );
}
